import type { ReactNode } from 'react'

import { paymentAssets } from '../../../constants/assets'
import { paymentTxnStatusLabels } from '../../../data/payments'
import type { PaymentRecord } from '../../../types'
import { formatEuro } from '../../../utils/format'
import { PaymentStatusBadge } from './PaymentStatusBadge'

type PaymentDetailsPanelProps = {
  payment: PaymentRecord | null
  onDownload?: () => void
  onRefund?: () => void
  onSendReceipt?: () => void
}
const DetailRow = ({
  label,
  value,
  labelWeight = 'normal',
}: {
  label: string
  value: string
  labelWeight?: 'normal' | 'medium'
}) => (
  <div className="flex w-full items-center justify-between gap-[12px] whitespace-nowrap font-host text-[12px] leading-normal text-[#7D7D7D]">
    <span className={labelWeight === 'medium' ? 'font-medium' : 'font-normal'}>{label}</span>
    <span className="truncate font-normal">{value}</span>
  </div>
)

const Section = ({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) => (
  <div className="flex w-full flex-col items-start gap-[6px]">
    <h3 className="w-full font-host text-[14px] font-semibold leading-normal text-[#403E3E]">
      {title}
    </h3>
    <div className="flex w-full flex-col items-start gap-[6px]">{children}</div>
  </div>
)

const moneyOrDash = (value: number | null | undefined) =>
  value == null ? '�' : formatEuro(value)

const shortDate = (iso: string) =>
  new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(
    new Date(iso),
  )

const gatewayStatusLabel = (status: PaymentRecord['status']) => {
  if (status === 'paid') return 'Successful'
  return paymentTxnStatusLabels[status]
}

export const PaymentDetailsPanel = ({
  payment,
  onDownload,
  onRefund,
  onSendReceipt,
}: PaymentDetailsPanelProps) => {
  if (!payment) {
    return (
      <aside className="flex h-full min-h-[420px] w-full flex-col items-center justify-center rounded-[14px] bg-white px-[24px] text-center shadow-[0px_1px_10.9px_0px_rgba(199,199,199,0.25)] xl:w-[384px] xl:shrink-0">
        <p className="font-host text-[16px] font-medium text-dash-heading">Payment Details</p>
        <p className="mt-[8px] font-host text-[14px] text-dash-body">
          Select a payment row to view its details.
        </p>
      </aside>
    )
  }

  const copyTransactionId = async () => {
    try {
      await navigator.clipboard.writeText(payment.transactionId)
    } catch {
      window.alert(payment.transactionId)
    }
  }

  return (
   <aside className="flex min-h-0 w-full flex-col overflow-hidden rounded-[14px] bg-white shadow-[0px_1px_10.9px_0px_rgba(199,199,199,0.25)] xl:h-full xl:w-[384px] xl:shrink-0">
      <div className="flex min-h-0 flex-1 flex-col gap-[15px] overflow-y-auto overscroll-contain px-[19px] pb-[16px] pt-[16px] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <div className="flex w-full items-start justify-between gap-[12px]">
          <div className="flex min-w-0 flex-col items-start gap-[9px]">
            <h2 className="font-host text-[16px] font-medium leading-normal text-[#070707]">
              Payment Details
            </h2>
            <div className="flex items-end gap-[11px]">
              <div className="flex flex-col items-start gap-[4px] font-host text-[12px] font-medium leading-normal text-black">
                <span>Transaction ID</span>
                <span>{payment.transactionId}</span>
              </div>
              <button
                type="button"
                aria-label="Copy transaction ID"
                onClick={copyTransactionId}
                className="block size-[17px] shrink-0"
              >
                <img
                  alt=""
                  aria-hidden
                  src={paymentAssets.copy}
                  className="block size-full max-w-none"
                />
              </button>
            </div>
          </div>

          <PaymentStatusBadge status={payment.status} />
        </div>

        <div className="flex w-full flex-col items-start gap-[15px]">
          <Section title="Transaction Information">
            <DetailRow label="Transaction ID" value={payment.transactionId} />
            <DetailRow label="Booking ID" value={payment.bookingId} />
            <DetailRow label="Customer" value={payment.customer.name} />
            <DetailRow label="Email" value={payment.customer.email} />
            <DetailRow label="Phone" value={payment.customer.phone} />
          </Section>

          <Section title="Payment Summary">
            <DetailRow
              label="Subtotal"
              value={moneyOrDash(payment.subtotalEur)}
            />
            <DetailRow
              label="Taxes"
              value={moneyOrDash(payment.taxesEur)}
            />
            <DetailRow
              label="Discount"
              value={moneyOrDash(payment.discountEur)}
            />
            <DetailRow
              label="Total Paid"
              value={
                payment.status === 'paid'
                  ? formatEuro(payment.amountEur)
                  : '�'
              }
            />
          </Section>

          <Section title="Payment Information">
            <DetailRow
              label="Payment Method"
              value={payment.methodLabel}
            />
            <DetailRow label="Payment Gateway" value={payment.gateway} />
            <DetailRow label="Transaction Date" value={shortDate(payment.paidAt)} />
            <div className="flex w-full items-center justify-between gap-[12px]">
              <span className="whitespace-nowrap font-host text-[12px] font-normal leading-normal text-[#7D7D7D]">
                Payment Status
              </span>
              <span className="flex items-center gap-[7px]">
                {payment.status === 'paid' && (
                  <img
                    alt=""
                    aria-hidden
                    src={paymentAssets.successDot}
                    className="block size-[12px] max-w-none"
                  />
                )}
                <span className="whitespace-nowrap font-host text-[12px] font-normal leading-normal text-[#7D7D7D]">
                  {gatewayStatusLabel(payment.status)}
                </span>
              </span>
            </div>
            <DetailRow label="Invoice" value={payment.invoice} />
          </Section>

          <Section title="Booking Details">
            <DetailRow label="Service" labelWeight="medium" value={payment.service} />
            <DetailRow label="Route" labelWeight="medium" value={payment.routeLabel} />
            <DetailRow label="Vehicle" labelWeight="medium" value={payment.vehicle} />
            <DetailRow label="Driver" labelWeight="medium" value={payment.driver} />
          </Section>
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-center gap-[9px] px-[12px] pb-[28px] pt-[16px]">
        <button
          type="button"
          onClick={onDownload}
          disabled={!onDownload}
          title="Invoice download API is not implemented yet."
          className="flex h-[31px] w-[91px] items-center justify-center gap-[5px] rounded-[6px] border border-solid border-[#919191] bg-white"
        >
          <img
            alt=""
            aria-hidden
            src={paymentAssets.download}
            className="block size-[20px] max-w-none"
          />
          <span className="font-host text-[10px] font-normal leading-normal text-[#202020]">
            Download
          </span>
        </button>

        <button
          type="button"
          onClick={onRefund}
          disabled={!onRefund || payment.status !== 'paid'}
          title="Refund processing API is not implemented yet."
          className="flex h-[31px] w-[74px] items-center justify-center gap-[5px] rounded-[6px] border border-solid border-[#919191] bg-white disabled:opacity-50"
        >
          <img
            alt=""
            aria-hidden
            src={paymentAssets.refund}
            className="block size-[13px] max-w-none"
          />
          <span className="font-host text-[10px] font-normal leading-normal text-[#202020]">
            Refund
          </span>
        </button>

        <button
          type="button"
          onClick={onSendReceipt}
          disabled={!onSendReceipt}
          title="Receipt sending API is not implemented yet."
          className="flex h-[31px] w-[96px] items-center justify-center gap-[5px] rounded-[6px] border border-solid border-[#919191] bg-white disabled:opacity-50"
        >
          <img
            alt=""
            aria-hidden
            src={paymentAssets.send}
            className="block size-[13px] max-w-none"
          />
          <span className="font-host text-[10px] font-normal leading-normal text-[#202020]">
            Send Receipt
          </span>
        </button>
      </div>
    </aside>
  )
}
